'use client';

import { useEffect, useState } from 'react';
import type { Product } from '@libs/common-next';
import { LayoutContainer, ProductCard } from '@petsmart-ui/sparky';
import { getProductsByIds, type ProductHit, transformHitForProductTile } from '@libs/algolia';
import type { ProductCarouselProps } from './types';
import { ProductTracker } from './ProductTracker';
import SectionTracker from './SectionTracker';
import { Carousel } from './Carousel';
import { Section } from './Section';
import './ProductCarousel.scss';

export default function ProductCarousel({
    headline,
    products = [],
    variant = 'default',
    isInDrawer = false,
    ...props
}: ProductCarouselProps) {
    const [productHits, setProductHits] = useState<Product[] | ProductHit[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!products || products.length === 0) {
            return;
        }

        const fetchProducts = async () => {
            const hits = (await getProductsByIds(products)) ?? [];
            const sortedHits = hits
                .filter(Boolean)
                .sort((a, b) => products.indexOf(a.objectID) - products.indexOf(b.objectID));
            setProductHits(sortedHits);
            setIsLoading(false);
        };

        fetchProducts();
    }, [products]);

    if (isLoading || !productHits.length) {
        return null;
    }

    return (
        <LayoutContainer className={`product-carousel-container product-carousel-container--${variant}`}>
            <Section {...props} variant={variant} headline={headline}>
                <SectionTracker productHits={productHits}>
                    <Carousel variant={variant} productHits={productHits} isInDrawer={isInDrawer}>
                        {productHits.map((product, index) => {
                            const { id, ...productData } = transformHitForProductTile(product) as Product;

                            return (
                                <ProductTracker
                                    key={id}
                                    product={productData as Product}
                                    headline={headline}
                                    variant={variant}
                                    position={index + 1}
                                >
                                    <ProductCard data-testid="product-carousel-items" {...productData} />
                                </ProductTracker>
                            );
                        })}
                    </Carousel>
                </SectionTracker>
            </Section>
        </LayoutContainer>
    );
}
